import { FC, useMemo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint, useToasts } from "@brg/ui";
import { useController, useFormContext } from "react-hook-form";
import { useTheme } from "styled-components";
import { API } from "@brg/api";

import { AuthorT, ProjectFormObject } from "../../types";
import DesignerSelect from "./DesignerSelect";
import { bProfile } from "core/utils/bProfile";
import { useProfile } from "core/hooks/auth/useProfile";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "../../ProjectAuthorsEdit";
import { MAX_AUTHORS_COUNT } from "../../constants";
import ProjectAuthorsDisplay from "../../ProjectAuthorsDisplay";
import AuthorField from "./AuthorField";
import { ProjectEditPageGridRow, ProjectEditPageGridLabel } from "shared/components/ProjectEditPageGrid";
import { LabelRedStar } from "../../../designerEditView/styles";
import { useRouteMatch } from "react-router-dom";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";

type AuthorsSelectProps = {
  isPresentation?: boolean;
};

const AuthorsSelect: FC<AuthorsSelectProps> = ({ isPresentation }) => {
  const { t, i18n } = useTranslation();
  const profile = useProfile();
  const toasts = useToasts();
  const { getValues, setValue, watch } = useFormContext<ProjectFormObject>();
  const is834 = useBreakpoint(834);
  const theme = useTheme();
  const [localAuthors, setLocalAuthors] = useState<AuthorT[]>([]);
  const watchedAuthors = watch('authors') || [];
  const authors = localAuthors.length > 0 ? localAuthors : watchedAuthors;
  const { isFr } = useAppTester();
  // const coAuthors = watch('coAuthors') || [];

  const { field: { value: coAuthors, onChange: setCoauthors } } = useController({
    name: 'coAuthors',
  })
  const isProjectEditPage = !!useRouteMatch("/edit") || !!useRouteMatch("/books/edit");

  const processedAuthors = useMemo(() => {
    if (authors && authors.length > 0) {
      return authors.map(author => API.toLang(author, i18n.language));
    }
    return authors;
  }, [authors, i18n.language]);

  const formObject = { authors: processedAuthors };
  const project = formObject;

  useEffect(() => {
    if (watchedAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(watchedAuthors as any);
    }
  }, [watchedAuthors, localAuthors.length]);

  useEffect(() => {
    const formAuthors = getValues('authors');
    if (formAuthors && formAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(formAuthors as any);
    }
  }, [getValues, localAuthors.length]);

  const isOwnProject = useMemo(() => formObject.authors?.some(({ accountId }) => {
    return accountId === profile?.accountId
  }), [formObject.authors, profile])

  const canManageProjectAuthor = useMemo(
    () =>
      !!profile &&
      (bProfile.isModer(profile) ||
        bProfile.isMiddleModer(profile) ||
        bProfile.isSuperModer(profile)),
    [profile],
  );

  const shouldDisplayAuthorField = canManageProjectAuthor;

  const shouldDisplayCoAuthorsField = !isFr;

  const excludeAccountIds = useMemo(() => {
    const ids = [profile?.accountId, profile?.designerId];
    (processedAuthors || []).forEach((author) => {
      ids.push(author?.accountId, author?.designerId);
    });
    return [...new Set(ids.filter(Boolean))] as string[];
  }, [profile?.accountId, profile?.designerId, processedAuthors]);

  const getTrueIfCoauthorIsAuthor = useCallback((coauthors: AuthorT[]) => {
    const authorIds = new Set(
      (formObject.authors || [])
        .flatMap((author) => [author?.accountId, author?.designerId])
        .filter(Boolean),
    );
    if (authorIds.size < 1) {
      return coauthors.some((coauthor) =>
        coauthor.accountId === profile?.accountId || coauthor.designerId === profile?.designerId,
      );
    }
    return coauthors.some((coauthor) =>
      authorIds.has(coauthor.accountId) || authorIds.has(coauthor.designerId),
    );
  }, [formObject.authors, profile?.accountId, profile?.designerId])

  const deleteUserIfExist = (arr: AuthorT[], arr1: AuthorT[], fn: Function, justOne: boolean) => {
    if (arr.length && arr1.length) {
      if (justOne) {
        return (arr.find((i) => i.designerId === arr1[0].designerId)) ? fn([]) : fn(arr1);
      } else {
        return fn(arr1.filter(i => i.designerId !== arr[0].designerId));
      }
    }
  }

  const onProjectAuthorsChange = useCallback(
    ({ authors, action }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > 1) {
          toasts.error('Автор может быть только один!');
          return;
        }
      }

      setValue('authors', authors as any);
      setLocalAuthors(authors as any);
    },
    [toasts, setValue],
  );

  const onProjectCoAuthorsChange = useCallback(
    ({ authors, action, removedValue }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > MAX_AUTHORS_COUNT) {
          toasts.error(`Нельзя добавить больше ${MAX_AUTHORS_COUNT} соавторов!`);
          return;
        }
      } else if (action === 'remove-value' || action === 'pop-value') {
        if (!(removedValue?.accountId === profile?.accountId
          || bProfile.isModer(profile)
          || bProfile.isMiddleModer(profile)
          || isOwnProject)) {
          return;
        }
      }
      if (action === 'select-option' && getTrueIfCoauthorIsAuthor(authors.slice(-1))) {
        toasts.error(t('toasts.project_coauthor_error'));
        return;
      }

      deleteUserIfExist(authors as any, formObject.authors as any, (val: any) => setValue('authors', val), true);
      setCoauthors(authors as any)

      // setValue('coAuthors', authors as any);
    },
    [formObject.authors, coAuthors, t, profile, toasts, isOwnProject, getTrueIfCoauthorIsAuthor],
  );

  return (
    <>
      {shouldDisplayAuthorField && isPresentation && (
        <ProjectAuthorsEdit
          label={t('project_edit.author')}
          required
          value={(formObject.authors || []) as AuthorT[]}
          onChange={onProjectAuthorsChange}
          placeholder={t('search_by_name')}
          persist={is834}
          isPresentation={isPresentation}
          isProjectEdit
          ignoreContext={true}
          canRemoveUser={true}
          customStyles={{
            backgroundColor: theme.projectEdit.inputs.bg,
            color: theme.projectEdit.inputs.color,
            placeholder: theme.projectEdit.inputs.placeholder,
          }}
        />
      )}
      {shouldDisplayAuthorField && !isPresentation && (
        <ProjectEditPageGridRow isProjectEdit={isProjectEditPage}>
          <ProjectEditPageGridLabel noMarginBottom>
            {t('project_edit.author')}
            <LabelRedStar>*</LabelRedStar>
          </ProjectEditPageGridLabel>
          {formObject.authors && formObject.authors.length > 0 ? (
            <ProjectAuthorsDisplay
              authors={(formObject.authors || []) as any}
              editing={false}
              onAddClick={() => { }}
              onRemoveClick={(authors: AuthorT[], { action, removedValue }: { action: string, removedValue: AuthorT }) => {
                onProjectAuthorsChange({ authors, action: 'remove-value' });
              }}
              isProjectEdit={true}
              canRemoveUser={true}
            />
          ) : (
            <AuthorField
              value={(formObject.authors || []) as any}
              onChange={(authors: AuthorT[]) => {
                if (authors.length > 0) {
                  onProjectAuthorsChange({ authors, action: 'select-option' });
                } else {
                  onProjectAuthorsChange({ authors, action: 'remove-value' });
                }
              }}
              required
              placeholder={t('search_by_name')}
              customStyles={{
                backgroundColor: theme.projectEdit.inputs.bg,
                color: theme.projectEdit.inputs.color,
                placeholder: theme.projectEdit.inputs.placeholder,
              }}
            />
          )}
        </ProjectEditPageGridRow>
      )}
      {shouldDisplayCoAuthorsField && (
        <DesignerSelect
          name="coAuthors"
          label={t('project_edit.coauthors')}
          ignoreContext={true}
          onChange={onProjectCoAuthorsChange}
          placeholder={t('search_by_name')}
          persist={is834}
          isPresentation={isPresentation}
          isProjectEdit
          excludeAccountIds={excludeAccountIds}
          customStyles={{
            backgroundColor: theme.projectEdit.inputs.bg,
            color: theme.projectEdit.inputs.color,
            placeholder: theme.projectEdit.inputs.placeholder,
          }}
        />
      )}
    </>
  );
};

export default AuthorsSelect;
import { FC, useMemo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint, useToasts } from "@brg/ui";
import { useController, useFormContext } from "react-hook-form";
import { useTheme } from "styled-components";
import { API } from "@brg/api";

import { AuthorT, ProjectFormObject } from "../../types";
import DesignerSelect from "./DesignerSelect";
import { bProfile } from "core/utils/bProfile";
import { useProfile } from "core/hooks/auth/useProfile";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "../../ProjectAuthorsEdit";
import { MAX_AUTHORS_COUNT } from "../../constants";
import ProjectAuthorsDisplay from "../../ProjectAuthorsDisplay";
import AuthorField from "./AuthorField";
import { ProjectEditPageGridRow, ProjectEditPageGridLabel } from "shared/components/ProjectEditPageGrid";
import { LabelRedStar } from "../../../designerEditView/styles";
import { useRouteMatch } from "react-router-dom";
import useAppTester from "shared/MobileMenu/hooks/useAppTester";

type AuthorsSelectProps = {
  isPresentation?: boolean;
};

const AuthorsSelect: FC<AuthorsSelectProps> = ({ isPresentation }) => {
  const { t, i18n } = useTranslation();
  const profile = useProfile();
  const toasts = useToasts();
  const { getValues, setValue, watch } = useFormContext<ProjectFormObject>();
  const is834 = useBreakpoint(834);
  const theme = useTheme();
  const [localAuthors, setLocalAuthors] = useState<AuthorT[]>([]);
  const watchedAuthors = watch('authors') || [];
  const authors = localAuthors.length > 0 ? localAuthors : watchedAuthors;
  const { isFr } = useAppTester();
  // const coAuthors = watch('coAuthors') || [];

  const { field: { value: coAuthors, onChange: setCoauthors } } = useController({
    name: 'coAuthors',
  })
  const isProjectEditPage = !!useRouteMatch("/edit") || !!useRouteMatch("/books/edit");

  const processedAuthors = useMemo(() => {
    if (authors && authors.length > 0) {
      return authors.map(author => API.toLang(author, i18n.language));
    }
    return authors;
  }, [authors, i18n.language]);

  const formObject = { authors: processedAuthors };
  const project = formObject;

  useEffect(() => {
    if (watchedAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(watchedAuthors as any);
    }
  }, [watchedAuthors, localAuthors.length]);

  useEffect(() => {
    const formAuthors = getValues('authors');
    if (formAuthors && formAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(formAuthors as any);
    }
  }, [getValues, localAuthors.length]);

  const isOwnProject = useMemo(() => formObject.authors?.some(({ accountId }) => {
    return accountId === profile?.accountId
  }), [formObject.authors, profile])

  const canManageProjectAuthor = useMemo(
    () =>
      !!profile &&
      (bProfile.isModer(profile) ||
        bProfile.isMiddleModer(profile) ||
        bProfile.isSuperModer(profile)),
    [profile],
  );

  const shouldDisplayAuthorField = canManageProjectAuthor;

  const shouldDisplayCoAuthorsField = !isFr;

  const excludeAccountIds = useMemo(() => {
    const ids = [profile?.accountId, profile?.designerId];
    (processedAuthors || []).forEach((author) => {
      ids.push(author?.accountId, author?.designerId);
    });
    return [...new Set(ids.filter(Boolean))] as string[];
  }, [profile?.accountId, profile?.designerId, processedAuthors]);

  const getTrueIfCoauthorIsAuthor = useCallback((coauthors: AuthorT[]) => {
    const authorIds = new Set(
      (formObject.authors || [])
        .flatMap((author) => [author?.accountId, author?.designerId])
        .filter(Boolean),
    );
    if (authorIds.size < 1) {
      return coauthors.some((coauthor) =>
        coauthor.accountId === profile?.accountId || coauthor.designerId === profile?.designerId,
      );
    }
    return coauthors.some((coauthor) =>
      authorIds.has(coauthor.accountId) || authorIds.has(coauthor.designerId),
    );
  }, [formObject.authors, profile?.accountId, profile?.designerId])

  const deleteUserIfExist = (arr: AuthorT[], arr1: AuthorT[], fn: Function, justOne: boolean) => {
    if (arr.length && arr1.length) {
      if (justOne) {
        return (arr.find((i) => i.designerId === arr1[0].designerId)) ? fn([]) : fn(arr1);
      } else {
        return fn(arr1.filter(i => i.designerId !== arr[0].designerId));
      }
    }
  }

  const onProjectAuthorsChange = useCallback(
    ({ authors, action }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > 1) {
          toasts.error('Автор может быть только один!');
          return;
        }
      }

      setValue('authors', authors as any);
      setLocalAuthors(authors as any);
    },
    [toasts, setValue],
  );

  const onProjectCoAuthorsChange = useCallback(
    ({ authors, action, removedValue }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > MAX_AUTHORS_COUNT) {
          toasts.error(`Нельзя добавить больше ${MAX_AUTHORS_COUNT} соавторов!`);
          return;
        }
      } else if (action === 'remove-value' || action === 'pop-value') {
        if (!(removedValue?.accountId === profile?.accountId
          || bProfile.isModer(profile)
          || bProfile.isMiddleModer(profile)
          || isOwnProject)) {
          return;
        }
      }
      if (action === 'select-option' && getTrueIfCoauthorIsAuthor(authors.slice(-1))) {
        toasts.error(t('toasts.project_coauthor_error'));
        return;
      }

      deleteUserIfExist(authors as any, formObject.authors as any, (val: any) => setValue('authors', val), true);
      setCoauthors(authors as any)

      // setValue('coAuthors', authors as any);
    },
    [formObject.authors, coAuthors, t, profile, toasts, isOwnProject, getTrueIfCoauthorIsAuthor],
  );

  return (
    <>
      {shouldDisplayAuthorField && isPresentation && (
        <ProjectAuthorsEdit
          label={t('project_edit.author')}
          required
          value={(formObject.authors || []) as AuthorT[]}
          onChange={onProjectAuthorsChange}
          placeholder={t('search_by_name')}
          persist={is834}
          isPresentation={isPresentation}
          isProjectEdit
          ignoreContext={true}
          canRemoveUser={true}
          customStyles={{
            backgroundColor: theme.projectEdit.inputs.bg,
            color: theme.projectEdit.inputs.color,
            placeholder: theme.projectEdit.inputs.placeholder,
          }}
        />
      )}
      {shouldDisplayAuthorField && !isPresentation && (
        <ProjectEditPageGridRow isProjectEdit={isProjectEditPage}>
          <ProjectEditPageGridLabel noMarginBottom>
            {t('project_edit.author')}
            <LabelRedStar>*</LabelRedStar>
          </ProjectEditPageGridLabel>
          {formObject.authors && formObject.authors.length > 0 ? (
            <ProjectAuthorsDisplay
              authors={(formObject.authors || []) as any}
              editing={false}
              onAddClick={() => { }}
              onRemoveClick={(authors: AuthorT[], { action, removedValue }: { action: string, removedValue: AuthorT }) => {
                onProjectAuthorsChange({ authors, action: 'remove-value' });
              }}
              isProjectEdit={true}
              canRemoveUser={true}
            />
          ) : (
            <AuthorField
              value={(formObject.authors || []) as any}
              onChange={(authors: AuthorT[]) => {
                if (authors.length > 0) {
                  onProjectAuthorsChange({ authors, action: 'select-option' });
                } else {
                  onProjectAuthorsChange({ authors, action: 'remove-value' });
                }
              }}
              required
              placeholder={t('search_by_name')}
              customStyles={{
                backgroundColor: theme.projectEdit.inputs.bg,
                color: theme.projectEdit.inputs.color,
                placeholder: theme.projectEdit.inputs.placeholder,
              }}
            />
          )}
        </ProjectEditPageGridRow>
      )}
      {shouldDisplayCoAuthorsField && (
        <DesignerSelect
          name="coAuthors"
          label={t('project_edit.coauthors')}
          ignoreContext={true}
          onChange={onProjectCoAuthorsChange}
          placeholder={t('search_by_name')}
          persist={is834}
          isPresentation={isPresentation}
          isProjectEdit
          excludeAccountIds={excludeAccountIds}
          customStyles={{
            backgroundColor: theme.projectEdit.inputs.bg,
            color: theme.projectEdit.inputs.color,
            placeholder: theme.projectEdit.inputs.placeholder,
          }}
        />
      )}
    </>
  );
};

export default AuthorsSelect;
